import {
    BellOutlined,
    CalendarOutlined,
    ExclamationCircleFilled,
} from "@ant-design/icons";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";

const { RangePicker } = DatePicker;

/* ════════════════════════════════════════════════════════════════
   DATA LAYER  (no UI here)
   ⚠ DUMMY DATA — replace `ALL_DUES` / `useSupplierDues` with the real
   API call. The UI only needs rows shaped like:
     { supplier, billNo, dueDate (YYYY-MM-DD), amount }
   Dates are relative to today so the demo always shows data.
   ════════════════════════════════════════════════════════════════ */
const fromToday = (n) => dayjs().add(n, "day").format("YYYY-MM-DD");

const ALL_DUES = [
    { billNo: "PI-2041", supplier: "Lakshmi Gold Works", dueDate: fromToday(-12), amount: 485000 },
    { billNo: "PI-2038", supplier: "Sri Balaji Jewels", dueDate: fromToday(-5), amount: 232000 },
    { billNo: "PI-2035", supplier: "Mahavir Bullion", dueDate: fromToday(0), amount: 712000 },
    { billNo: "PI-2031", supplier: "Kalyan Castings", dueDate: fromToday(2), amount: 125800 },
    { billNo: "PI-2028", supplier: "Venkat Silver House", dueDate: fromToday(4), amount: 93400 },
    { billNo: "PI-2024", supplier: "Lakshmi Gold Works", dueDate: fromToday(6), amount: 341600 },
    { billNo: "PI-2019", supplier: "Om Stone Traders", dueDate: fromToday(15), amount: 58900 },
    { billNo: "PI-2015", supplier: "Sri Balaji Jewels", dueDate: fromToday(28), amount: 137200 },
];

const useSupplierDues = (range) =>
    useMemo(() => {
        const from = range?.[0]?.startOf("day");
        const to = range?.[1]?.endOf("day");
        const today = dayjs().startOf("day");

        return ALL_DUES.filter((d) => {
            if (!from || !to) return true;
            const due = dayjs(d.dueDate);
            return !due.isBefore(from) && !due.isAfter(to);
        })
            .map((d) => ({
                ...d,
                daysLeft: dayjs(d.dueDate).startOf("day").diff(today, "day"), // <0 = overdue
            }))
            .sort((a, b) => a.daysLeft - b.daysLeft); // most overdue first
    }, [range]);

/* ════════════════════════════════════════════════════════════════
   UI
   ════════════════════════════════════════════════════════════════ */
const COLS = [
    { label: "Supplier", flex: 1 },
    { label: "Due", w: 78 },
    { label: "Amount", w: 66, right: true },
];

const fmtShort = (n) =>
    n >= 100000
        ? `${(n / 100000).toFixed(1)}L`
        : n >= 1000
            ? `${(n / 1000).toFixed(1)}k`
            : `${Number(n).toFixed(0)}`;

/* Severity: overdue = red, due today / within 7 days = amber, later = green */
const severityOf = (daysLeft) => {
    if (daysLeft < 0)
        return { color: "#e05252", bg: "#fff0f0", label: `Overdue ${-daysLeft}d` };
    if (daysLeft === 0)
        return { color: "#e05252", bg: "#fff0f0", label: "Due today" };
    if (daysLeft <= 7)
        return { color: "#e0a526", bg: "#fff8e6", label: `In ${daysLeft}d` };
    return { color: "#43aa8b", bg: "#eefaf5", label: `In ${daysLeft}d` };
};

/* ── Styled range picker that blends into the gradient header ── */
const HeaderRangePicker = ({ value, onChange }) => (
    <>
        <style>{`
      .due-rp .ant-picker {
        background: rgba(255,255,255,0.14) !important;
        border: 1px solid rgba(255,255,255,0.30) !important;
        border-radius: 20px !important;
        padding: 2px 10px !important;
        height: 24px !important;
        box-shadow: none !important;
      }
      .due-rp .ant-picker:hover,
      .due-rp .ant-picker-focused {
        border-color: rgba(255,255,255,0.55) !important;
        box-shadow: none !important;
      }
      .due-rp .ant-picker-input > input {
        color: #fff !important;
        font-size: 10px !important;
        font-weight: 700 !important;
        width: 56px !important;
      }
      .due-rp .ant-picker-input > input::placeholder { color: rgba(255,255,255,0.55) !important; }
      .due-rp .ant-picker-separator,
      .due-rp .ant-picker-suffix { color: rgba(255,255,255,0.6) !important; font-size: 10px !important; }
      .due-rp .ant-picker-clear { display: none !important; }
    `}</style>
        <div className="due-rp">
            <RangePicker
                value={value}
                onChange={onChange}
                format="DD MMM YY"
                size="small"
                allowClear={false}
                suffixIcon={
                    <CalendarOutlined
                        style={{ color: "rgba(255,255,255,0.7)", fontSize: 10 }}
                    />
                }
                popupStyle={{ zIndex: 9999 }}
            />
        </div>
    </>
);

const SupplierDueAlerts = () => {
    // Default window: last 30 days (to catch overdue) through next 30 days
    const [range, setRange] = useState([
        dayjs().subtract(30, "day"),
        dayjs().add(30, "day"),
    ]);
    const data = useSupplierDues(range);

    const overdue = data.filter((d) => d.daysLeft < 0);
    const totalDue = data.reduce((a, d) => a + d.amount, 0);
    const totalOverdue = overdue.reduce((a, d) => a + d.amount, 0);
    const hasOverdue = overdue.length > 0;

    return (
        <div
            style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 2px 14px rgba(44,27,105,0.10)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            {/* ── Gradient header — single row ── */}
            <div
                style={{
                    padding: "7px 14px",
                    background: "linear-gradient(90deg,#e05252 0%,#f4a261 100%)",
                    position: "relative",
                    overflow: "hidden",
                    minHeight: 38,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        right: -20,
                        top: -20,
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.07)",
                        pointerEvents: "none",
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        left: -10,
                        bottom: -15,
                        width: 50,
                        height: 50,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.04)",
                        pointerEvents: "none",
                    }}
                />

                {/* Left: icon + title */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <motion.div
                        animate={hasOverdue ? { rotate: [0, -14, 14, -8, 8, 0] } : {}}
                        transition={{ repeat: Infinity, duration: 2.4, repeatDelay: 1.6 }}
                        style={{
                            width: 26,
                            height: 26,
                            borderRadius: 7,
                            background: "rgba(255,255,255,0.18)",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <BellOutlined style={{ fontSize: 13, color: "#fff" }} />
                    </motion.div>
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#fff",
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Supplier Due Alerts
                        </div>
                        <div
                            style={{
                                fontSize: 9,
                                color: "rgba(255,255,255,0.8)",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {data.length} dues · {overdue.length} overdue · ₹
                            {fmtShort(totalDue)}
                        </div>
                    </div>
                </div>

                {/* Right: date range picker + pulse dot */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexShrink: 0,
                    }}
                >
                    <HeaderRangePicker value={range} onChange={(v) => v && setRange(v)} />
                    <motion.div
                        animate={{ scale: [1, 1.5, 1], opacity: [1, 0.4, 1] }}
                        transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                        style={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: hasOverdue ? "#fff" : "#90be6d",
                            flexShrink: 0,
                        }}
                    />
                </div>
            </div>

            {/* ── Column headers ── */}
            <div
                style={{
                    display: "flex",
                    gap: 6,
                    padding: "4px 14px",
                    background: "rgba(108,92,231,0.13)",
                    borderBottom: "1px solid rgba(108,92,231,0.15)",
                }}
            >
                {COLS.map((c) => (
                    <div
                        key={c.label}
                        style={{
                            width: c.w,
                            flex: c.flex,
                            fontSize: 9,
                            fontWeight: 800,
                            color: "#6c5ce7",
                            textTransform: "uppercase",
                            letterSpacing: 0.6,
                            textAlign: c.right ? "right" : "left",
                        }}
                    >
                        {c.label}
                    </div>
                ))}
            </div>

            {/* ── Rows ── */}
            <div style={{ overflowY: "auto", flex: 1, overflowX: "hidden" }}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={JSON.stringify(range)}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        {data.length === 0 ? (
                            <div
                                style={{
                                    textAlign: "center",
                                    padding: "20px 0",
                                    color: "#b8a4e8",
                                    fontSize: 11,
                                    fontWeight: 600,
                                }}
                            >
                                No supplier dues in this range
                            </div>
                        ) : (
                            <>
                                {data.map((r, i) => {
                                    const sev = severityOf(r.daysLeft);
                                    return (
                                        <motion.div
                                            key={r.billNo}
                                            initial={{ opacity: 0, x: -8 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: i * 0.04, duration: 0.25 }}
                                            whileHover={{
                                                borderLeft: `3px solid ${sev.color}`,
                                                paddingLeft: 11,
                                            }}
                                            style={{
                                                display: "flex",
                                                gap: 6,
                                                padding: "7px 14px",
                                                borderBottom: "1px solid #f0eaff",
                                                borderLeft: `3px solid ${sev.color}`,
                                                alignItems: "center",
                                                cursor: "default",
                                                background: i % 2 === 0 ? "#fff" : "#fafafa",
                                                transition: "background 0.15s, border-left 0.15s",
                                            }}
                                        >
                                            {/* Supplier */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        color: "#2d1b69",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 4,
                                                    }}
                                                >
                                                    {r.daysLeft < 0 && (
                                                        <ExclamationCircleFilled
                                                            style={{ fontSize: 10, color: sev.color }}
                                                        />
                                                    )}
                                                    {r.supplier}
                                                </div>
                                                <div style={{ fontSize: 9, color: "#aaa" }}>
                                                    {r.billNo} · {dayjs(r.dueDate).format("DD MMM YY")}
                                                </div>
                                            </div>

                                            {/* Due status chip */}
                                            <div style={{ width: 78 }}>
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        fontSize: 9,
                                                        fontWeight: 800,
                                                        color: sev.color,
                                                        background: sev.bg,
                                                        borderRadius: 8,
                                                        padding: "2px 7px",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {sev.label}
                                                </span>
                                            </div>

                                            {/* Amount */}
                                            <div style={{ width: 66, textAlign: "right" }}>
                                                <span
                                                    style={{
                                                        fontSize: 9,
                                                        fontWeight: 700,
                                                        color: sev.color,
                                                    }}
                                                >
                                                    ₹
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 900,
                                                        color: sev.color,
                                                    }}
                                                >
                                                    {fmtShort(r.amount)}
                                                </span>
                                            </div>
                                        </motion.div>
                                    );
                                })}

                                {/* Totals row */}
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 6,
                                        padding: "6px 14px",
                                        background: "rgba(108,92,231,0.07)",
                                        borderLeft: "3px solid transparent",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            flex: 1,
                                            fontSize: 9,
                                            fontWeight: 800,
                                            color: "#6c5ce7",
                                            textTransform: "uppercase",
                                            letterSpacing: 0.6,
                                        }}
                                    >
                                        Total{hasOverdue ? ` · ₹${fmtShort(totalOverdue)} overdue` : ""}
                                    </div>
                                    <div style={{ width: 78 }} />
                                    <div
                                        style={{
                                            width: 66,
                                            textAlign: "right",
                                            fontSize: 12,
                                            fontWeight: 900,
                                            color: "#4c3494",
                                        }}
                                    >
                                        ₹{fmtShort(totalDue)}
                                    </div>
                                </div>
                            </>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default SupplierDueAlerts;